/**
 * ModuleLoader.jsx — ELYON-SHELL-FOUNDATION-1
 *
 * Dynamically imports module manifests for each enabled module ID
 * in the tenant config. Returns { manifests, loading }.
 *
 * The shell is module-agnostic (§1.2 BINDING): it reads enabled_modules[]
 * from tenantConfig, imports manifests dynamically, and builds nav from
 * what manifests declare. Zero hard-coded module IDs or routes here.
 *
 * Build strategy:
 *   Every `src/modules/*\/manifest.js` is discovered at BUILD time via
 *   import.meta.glob, so Vite bundles each as a lazy chunk that loads correctly
 *   in production. This replaces the previous `@vite-ignore` template-literal
 *   dynamic import, which Vite excluded from the bundle — at runtime the browser
 *   requested `../modules/<id>/manifest.js` as a static path, got the SPA
 *   fallback HTML, and threw "Expected a JavaScript module but got text/html".
 *   A module with no manifest.js is simply absent from the glob map, so
 *   loadModuleManifest returns null without any network request (no MIME error).
 *
 *   command-center keeps an explicit eager static import: its manifest's screen
 *   import is intentionally @vite-ignore'd to break a circular dep (TDZ crash),
 *   and the eager path is the proven default-landing route — left exactly as-is.
 *
 * Adding a new module: just drop a `manifest.js` in src/modules/<id>/ and add
 * the id to SUPPORTED_MODULE_IDS. No loader edit needed (glob picks it up).
 */
import { useState, useEffect } from 'react';
import { useTenant } from './TenantContext.jsx';

// ── command-center: eager static (proven default-landing path) ──────────────
import commandCenterManifest from '../modules/command-center/manifest.js';
import tmsManifest from '../modules/tms/manifest.js'; // ELYON-TMS-V2-B1-1
import iotManifest from '../modules/iot/manifest.js';

/**
 * Manifests for shipped modules — Vite statically bundles these.
 * Add here when a module's manifest.js lands in the repo.
 *
 * IMPORTANT: the dynamic @vite-ignore fallback in loadModuleManifest() is
 * EXCLUDED from the production dist. A shipped module MUST be registered here
 * or it loads in `vite dev` but silently 404s in production — invisible in the
 * v2 shell sidebar even when it is listed in tenant.enabled_modules.
 */
const STATIC_MANIFESTS = {
  'command-center': commandCenterManifest,
  'tms': tmsManifest, // ELYON-TMS-V2-B1-1
  iot: iotManifest,
};

/**
 * Build-time map of every module manifest: { '../modules/<id>/manifest.js': () => import(...) }.
 * Vite bundles each target as a real lazy chunk — production-safe.
 */
const GLOB_MANIFEST_LOADERS = import.meta.glob('../modules/*/manifest.js');

/** All module IDs the shell recognises (shipped + unshipped). */
const SUPPORTED_MODULE_IDS = new Set([
  'command-center',
  'tms', 'iot', 'intl', 'cx', 'crm', 'oms', 'wms', 'ims',
  'fleet', 'fin', 'doc', 'nav', 'comms', 'it', 'driver',
  'pylon', 'aegis', 'settings',
]);

/**
 * Loads a single module manifest by ID.
 * Shipped modules use the static map; unshipped use @vite-ignore and
 * resolve to null if the file doesn't exist yet.
 *
 * @param {string} id
 * @returns {Promise<object|null>}
 */
function loadModuleManifest(id) {
  if (STATIC_MANIFESTS[id]) {
    return Promise.resolve(STATIC_MANIFESTS[id]);
  }
  // Build-time-bundled lazy chunk (production-safe). Absent → no manifest for
  // this module yet; resolve null without a network request (no MIME error).
  const loader = GLOB_MANIFEST_LOADERS[`../modules/${id}/manifest.js`];
  if (!loader) return Promise.resolve(null);
  return loader()
    .then((m) => m.default)
    .catch(() => null);
}

/**
 * Hook: loads manifests for all enabled modules in the tenant config.
 *
 * @returns {{ manifests: object[], loading: boolean }}
 */
export function useModuleManifests() {
  const { tenantConfig } = useTenant();
  const [manifests, setManifests] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    const enabledModules = (tenantConfig?.enabled_modules ?? [])
      .filter((id) => SUPPORTED_MODULE_IDS.has(id));

    if (enabledModules.length === 0) {
      setManifests([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    Promise.all(enabledModules.map(loadModuleManifest))
      .then((loaded) => {
        setManifests(loaded.filter(Boolean));
        setLoading(false);
      });
  }, [tenantConfig]);

  return { manifests, loading };
}
